import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator, Copy, Trash2, TrendingUp, TrendingDown, CheckCircle2, ArrowRight, ChevronDown, Coins, Hexagon } from 'lucide-react';
import CryptoAffiliateBlock from '../../../components/CryptoAffiliateBlock';
import RiskAnalysisBlock from '../../../components/RiskAnalysisBlock';

const PolygonProfitCalculatorTool = () => {
  const [buyPrice, setBuyPrice] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [investment, setInvestment] = useState('');
  const [fees, setFees] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');
  const [showCoinMenu, setShowCoinMenu] = useState(false);

  useEffect(() => {
    calculate();
  }, [buyPrice, sellPrice, investment, fees]);

  const calculate = () => {
    const buy = parseFloat(buyPrice);
    const sell = parseFloat(sellPrice);
    const invest = parseFloat(investment);
    const fee = parseFloat(fees) || 0;

    if (isNaN(buy) || isNaN(sell) || isNaN(invest) || buy <= 0 || invest <= 0) {
      setResult(null);
      return;
    }

    const maticPurchased = invest / buy;
    const revenue = maticPurchased * sell;
    const grossProfit = revenue - invest;
    const netProfit = grossProfit - fee;
    const roi = (netProfit / invest) * 100;
    const breakEven = (invest + fee) / maticPurchased;

    setResult({
      maticPurchased,
      revenue,
      grossProfit,
      netProfit,
      roi,
      breakEven,
      isProfitable: netProfit > 0,
      isBreakEven: Math.abs(netProfit) < 0.01
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(typeof value === 'number' ? value.toFixed(2) : value);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setBuyPrice('');
    setSellPrice('');
    setInvestment('');
    setFees('');
    setResult(null);
  };

  const formatCurrency = (value) => '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const formatPercent = (value) => (value >= 0 ? '+' : '') + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

  const scenarios = [
    { label: 'MATIC: $0.50 → $1.20', buy: '0.50', sell: '1.20', invest: '1000' },
    { label: 'MATIC: $0.80 → $2.00', buy: '0.80', sell: '2.00', invest: '2000' },
    { label: 'MATIC: $0.60 → $1.80', buy: '0.60', sell: '1.80', invest: '1500' },
  ];

  const applyScenario = (scenario) => {
    setBuyPrice(scenario.buy);
    setSellPrice(scenario.sell);
    setInvestment(scenario.invest);
    setFees('5');
  };

  const coinOptions = [
    { name: 'Polygon (MATIC)', slug: 'polygon-profit-calculator', icon: '⬡', current: true },
    { name: 'Ethereum (ETH)', slug: 'ethereum-profit-calculator', icon: 'Ξ', current: false },
    { name: 'Bitcoin (BTC)', slug: 'bitcoin-profit-calculator', icon: '₿', current: false },
    { name: 'Solana (SOL)', slug: 'solana-profit-calculator', icon: '◎', current: false },
    { name: 'Cardano (ADA)', slug: 'cardano-profit-calculator', icon: '₳', current: false },
    { name: 'All Crypto', slug: 'crypto-profit-calculator', icon: '🪙', current: false },
  ];

  return (
    <div className="space-y-8" data-testid="polygon-profit-calculator">
      {/* Coin Selector */}
      <div className="relative">
        <button
          onClick={() => setShowCoinMenu(!showCoinMenu)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 border border-purple-500/30 rounded-lg text-purple-400 hover:bg-purple-500/30 transition-colors"
          data-testid="coin-selector"
        >
          <span className="text-xl font-bold">⬡</span>
          <span className="font-medium">Polygon Profit Calculator</span>
          <ChevronDown size={16} className={`transition-transform ${showCoinMenu ? 'rotate-180' : ''}`} />
        </button>
        
        {showCoinMenu && (
          <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50">
            {coinOptions.map((coin) => (
              coin.current ? (
                <div key={coin.slug} className="flex items-center gap-3 px-4 py-3 bg-purple-500/10 text-purple-400 cursor-default">
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <CheckCircle2 size={16} className="ml-auto" />
                </div>
              ) : (
                <Link
                  key={coin.slug}
                  to={`/tools/${coin.slug}`}
                  className="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700 transition-colors"
                  onClick={() => setShowCoinMenu(false)}
                >
                  <span className="text-xl">{coin.icon}</span>
                  <span>{coin.name}</span>
                  <ArrowRight size={16} className="ml-auto opacity-50" />
                </Link>
              )
            ))}
          </div>
        )}
      </div>

      {/* Quick Examples */}
      <div className="flex flex-wrap gap-2">
        <span className="text-sm text-gray-400 mr-2">Quick Examples:</span>
        {scenarios.map((scenario, index) => (
          <button
            key={index}
            onClick={() => applyScenario(scenario)}
            className="px-3 py-1 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-full text-gray-300 transition-colors"
          >
            {scenario.label}
          </button>
        ))}
      </div>

      {/* Calculator */}
      <div className="p-6 bg-gray-800 rounded-xl space-y-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
            <span className="text-2xl font-bold text-purple-400">⬡</span>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Polygon Profit Calculator</h3>
            <p className="text-sm text-gray-400">Calculate your MATIC investment profit — Ethereum's scaling solution</p>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Buy Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.001"
                min="0"
                value={buyPrice}
                onChange={(e) => setBuyPrice(e.target.value)}
                placeholder="e.g., 0.70"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none text-lg"
                data-testid="buy-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Sell Price (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.001"
                min="0"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="e.g., 1.50"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none text-lg"
                data-testid="sell-price-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Investment Amount (USD) <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={investment}
                onChange={(e) => setInvestment(e.target.value)}
                placeholder="e.g., 1000"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none text-lg"
                data-testid="investment-input"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Trading Fees (USD) <span className="text-gray-500">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={fees}
                onChange={(e) => setFees(e.target.value)}
                placeholder="e.g., 5"
                className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-8 pr-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Polygon fees are extremely low (~$0.001-0.01)</p>
          </div>
        </div>

        <button
          onClick={clearAll}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-gray-300 transition-colors text-sm"
        >
          <Trash2 size={14} />
          Clear All
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl border-2 ${result.isProfitable ? 'bg-green-500/10 border-green-500/40' : result.isBreakEven ? 'bg-yellow-500/10 border-yellow-500/40' : 'bg-red-500/10 border-red-500/40'}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                {result.isProfitable ? <TrendingUp className="text-green-400" size={28} /> : <TrendingDown className="text-red-400" size={28} />}
                <span className="text-lg font-medium text-gray-200">
                  {result.isProfitable ? 'Profit' : result.isBreakEven ? 'Break Even' : 'Loss'}
                </span>
              </div>
              <button onClick={() => copyResult(result.netProfit, 'profit')} className="p-2 hover:bg-gray-700 rounded-lg">
                {copied === 'profit' ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} className="text-gray-400" />}
              </button>
            </div>
            <div className="text-center">
              <p className={`text-5xl font-bold ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {result.netProfit >= 0 ? '+' : ''}{formatCurrency(result.netProfit)}
              </p>
              <p className={`text-2xl mt-2 ${result.isProfitable ? 'text-green-400' : result.isBreakEven ? 'text-yellow-400' : 'text-red-400'}`}>
                {formatPercent(result.roi)} ROI
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">MATIC Purchased</p>
              <p className="text-xl font-semibold text-purple-400">{result.maticPurchased.toFixed(2)} MATIC</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Sale Value</p>
              <p className="text-xl font-semibold text-white">{formatCurrency(result.revenue)}</p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Gross Profit</p>
              <p className={`text-xl font-semibold ${result.grossProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {result.grossProfit >= 0 ? '+' : ''}{formatCurrency(result.grossProfit)}
              </p>
            </div>
            <div className="p-4 bg-gray-800 rounded-lg text-center">
              <p className="text-sm text-gray-400 mb-1">Break-even Price</p>
              <p className="text-xl font-semibold text-orange-400">{formatCurrency(result.breakEven)}</p>
            </div>
          </div>

          <CryptoAffiliateBlock 
            profit={result.netProfit} 
            cryptoName="MATIC"
            toolName="Polygon Profit Calculator"
            toolSlug="polygon-profit-calculator"
          />
        </div>
      )}

      {/* Related Calculators */}
      <div className="p-5 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Coins size={20} className="text-purple-400" />
          Related Crypto Calculators
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link to="/tools/crypto-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-green-400">Crypto Profit</p>
              <p className="text-sm text-gray-400">All coins</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-green-400" />
          </Link>
          <Link to="/tools/ethereum-profit-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-indigo-400">Ethereum Profit</p>
              <p className="text-sm text-gray-400">ETH (L1)</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-indigo-400" />
          </Link>
          <Link to="/tools/crypto-roi-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-blue-400">Crypto ROI</p>
              <p className="text-sm text-gray-400">Return analysis</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-blue-400" />
          </Link>
          <Link to="/tools/crypto-tax-calculator" className="flex items-center justify-between p-4 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors group">
            <div>
              <p className="font-medium text-white group-hover:text-purple-400">Crypto Tax</p>
              <p className="text-sm text-gray-400">Tax estimates</p>
            </div>
            <ArrowRight size={18} className="text-gray-500 group-hover:text-purple-400" />
          </Link>
        </div>
      </div>

      {/* SEO Content */}
      <div className="space-y-8 pt-6 border-t border-gray-700">
        <section>
          <h2 className="text-2xl font-bold text-white mb-4">What is a Polygon Profit Calculator?</h2>
          <div className="prose prose-invert max-w-none text-gray-300 space-y-4">
            <p>
              A <strong>Polygon Profit Calculator</strong> is an essential tool for MATIC investors to calculate 
              profits from trading Polygon, Ethereum's leading Layer 2 scaling solution. Polygon (formerly Matic Network) 
              solves Ethereum's scalability issues by processing transactions off the main chain while inheriting 
              Ethereum's security. This makes transactions faster (up to 65,000 TPS) and cheaper (fractions of a cent) 
              while maintaining compatibility with Ethereum's ecosystem.
            </p>
            <p>
              Our <strong>MATIC profit calculator</strong> helps you determine your profit or loss by entering your 
              buy price, sell price, and investment amount. As the native token of Polygon, MATIC is used for gas 
              fees, staking, and governance. The calculator instantly shows your total MATIC acquired, sale value, 
              net profit, and ROI — essential metrics for evaluating your Layer 2 investment.
            </p>
            <p>
              Whether you're trading MATIC on centralized exchanges, using it for DeFi on Polygon's network, or 
              holding long-term as a bet on Ethereum scaling, this calculator provides accurate profit projections. 
              With major partnerships (Disney, Meta, Starbucks, Nike) and a thriving ecosystem of DeFi protocols 
              and NFT marketplaces, Polygon represents a key piece of Ethereum's scaling roadmap.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">How to Use the Polygon Profit Calculator</h2>
          <div className="space-y-3">
            {[
              { step: 1, title: 'Enter Your MATIC Buy Price', desc: 'Input the price per MATIC when you purchased. Polygon typically trades between $0.30-$2.50 depending on market conditions.' },
              { step: 2, title: 'Enter Your MATIC Sell Price', desc: 'Input the price at which you sold (or plan to sell). Check real-time MATIC prices on exchanges or aggregators.' },
              { step: 3, title: 'Enter Your Investment Amount', desc: 'The total USD invested. The calculator shows how many MATIC tokens you acquired at your entry price.' },
              { step: 4, title: 'Add Trading Fees (Optional)', desc: 'Include exchange fees. Polygon network fees are negligible (~$0.001-0.01), but exchange fees vary (0.1%-0.5%).' },
              { step: 5, title: 'View Your Results', desc: 'Instantly see your profit/loss, ROI percentage, MATIC quantity, and break-even price for informed decision making.' },
            ].map((item) => (
              <div key={item.step} className="flex gap-4 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 text-purple-400 flex items-center justify-center font-bold flex-shrink-0">{item.step}</div>
                <div>
                  <h3 className="font-semibold text-white">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Example Polygon Profit Calculation</h2>
          <div className="p-5 bg-gray-800 rounded-lg border border-gray-700">
            <h3 className="font-semibold text-white mb-3">Scenario: MATIC Trade During L2 Adoption Wave</h3>
            <div className="space-y-3 text-gray-300">
              <p>Let's calculate profit from a Polygon trade during increased Layer 2 adoption:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Buy Price:</strong> $0.65 per MATIC</li>
                <li><strong>Sell Price:</strong> $1.80 per MATIC</li>
                <li><strong>Investment:</strong> $1,300</li>
                <li><strong>Trading Fees:</strong> $6.50 (0.5% total)</li>
              </ul>
              <div className="mt-4 p-4 bg-gray-900 rounded-lg font-mono text-sm">
                <p><span className="text-gray-400">MATIC Purchased:</span> <span className="text-purple-400">$1,300 / $0.65 = 2,000 MATIC</span></p>
                <p><span className="text-gray-400">Sale Value:</span> <span className="text-white">2,000 MATIC × $1.80 = $3,600</span></p>
                <p><span className="text-gray-400">Gross Profit:</span> <span className="text-green-400">$3,600 - $1,300 = $2,300</span></p>
                <p><span className="text-gray-400">Net Profit:</span> <span className="text-green-400">$2,300 - $6.50 = $2,293.50</span></p>
                <p><span className="text-gray-400">ROI:</span> <span className="text-blue-400">($2,293.50 / $1,300) × 100 = 176.4%</span></p>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                This trade turned $1,300 into $3,593.50 after fees — a 176.4% return. MATIC can also be staked on 
                Polygon's network for additional rewards (typically 5-10% APY).
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-white mb-4">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {[
              { q: 'What is the difference between Polygon and Ethereum?', a: 'Ethereum is the main blockchain (Layer 1); Polygon is a Layer 2 scaling solution built on top of it. Polygon processes transactions faster and cheaper while using Ethereum for security. Think of Ethereum as the highway and Polygon as an express lane.' },
              { q: 'Why are Polygon fees so low?', a: 'Polygon processes transactions in batches off Ethereum\'s main chain, then posts summaries back. This reduces congestion and costs. Typical Polygon fees are $0.001-0.01 vs $5-50+ on Ethereum during high demand.' },
              { q: 'What was MATIC\'s all-time high?', a: 'MATIC reached approximately $2.92 in December 2021 during the DeFi and NFT boom. Many investors use historical highs to set profit-taking targets.' },
              { q: 'Can I stake MATIC?', a: 'Yes! MATIC can be staked on Polygon\'s network to earn rewards (typically 5-10% APY) and help secure the network. You can stake directly through Polygon\'s staking dashboard or via validators.' },
              { q: 'Why do major brands use Polygon?', a: 'Polygon offers Ethereum compatibility with lower costs, making it ideal for NFTs and loyalty programs. Disney, Meta, Starbucks, Nike, and Reddit have all launched projects on Polygon due to its scalability and brand-safe environment.' },
            ].map((faq, i) => (
              <div key={i} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-gray-400 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="p-4 bg-gradient-to-r from-purple-500/10 to-indigo-500/10 rounded-lg border border-purple-500/20">
        <h4 className="text-sm font-medium text-gray-200 mb-2 flex items-center gap-2">
          <Hexagon size={16} className="text-purple-400" />
          Polygon's Enterprise Adoption
        </h4>
        <p className="text-sm text-gray-400">
          Polygon has become the go-to blockchain for enterprise adoption, with partnerships spanning Disney (NFTs), 
          Starbucks (loyalty program), Nike (digital collectibles), and Reddit (community points). This enterprise 
          adoption provides fundamental value beyond speculation — consider this when evaluating MATIC's long-term potential.
        </p>
      </div>
    </div>
  );
};

export default PolygonProfitCalculatorTool;
